/**
 * Where this kind of work happens, picked from a short list rather than typed
 * as a bare URL. A handle saved against the wrong host is not something we
 * notice later: we cannot tell "not a real page" from "typo'd the platform"
 * once it is sitting in a search that finds nothing.
 *
 * Shared between the profile wizard and the quick-start form — one list, so a
 * platform added to one is not missing from the other.
 */
export const PLATFORMS = [
  { key: 'onlyfans', label: 'OnlyFans', host: 'onlyfans.com' },
  { key: 'fansly', label: 'Fansly', host: 'fansly.com' },
  { key: 'instagram', label: 'Instagram', host: 'instagram.com' },
  { key: 'x', label: 'X', host: 'x.com' },
  { key: 'tiktok', label: 'TikTok', host: 'tiktok.com' },
  { key: 'reddit', label: 'Reddit', host: 'reddit.com' },
  { key: 'patreon', label: 'Patreon', host: 'patreon.com' },
  { key: 'manyvids', label: 'ManyVids', host: 'manyvids.com' },
  { key: 'other', label: 'Somewhere else', host: null },
] as const;

/**
 * The short list — the three places most of this trade actually posts, plus
 * "somewhere else" as the escape hatch. For the moment somebody has just
 * proved her email and has not yet decided the product is worth five minutes
 * of choosing among nine platforms: the full `PLATFORMS` list is still where
 * `/profiles` looks once there is a reason to be thorough.
 */
const QUICK_PLATFORM_KEYS = new Set(['onlyfans', 'fansly', 'instagram', 'other']);

/** What a handle may be typed for while the search knows one platform: a
 *  picker that takes a Fansly handle and searches it as an OnlyFans one is a
 *  promise we cannot keep. `PLATFORMS` stays whole — handles saved before this
 *  narrowed still have to be read back. */
const OFFERED_PLATFORM_KEYS = new Set(['onlyfans']);

export const OFFERED_PLATFORMS = PLATFORMS.filter((platform) =>
  OFFERED_PLATFORM_KEYS.has(platform.key),
);

export const QUICK_PLATFORMS = PLATFORMS.filter(
  (platform) => QUICK_PLATFORM_KEYS.has(platform.key) && OFFERED_PLATFORM_KEYS.has(platform.key),
);

/** The handle, turned into the page it names. "Somewhere else" has no host of
 *  its own, so there the box is asking for the full address directly. */
export function platformUrlFrom(platform: string, handle: string): string {
  const trimmed = handle.trim();
  if (!trimmed) return '';
  const known = PLATFORMS.find((entry) => entry.key === platform);
  if (!known?.host || /^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${known.host}/${trimmed.replace(/^[@/]+/, '')}`;
}

/** The platform key for an address we already hold, so the editor's picker
 *  opens on the platform the row is actually about. */
export function platformKeyOf(url: string, hostOf: (url: string) => string): string {
  const host = hostOf(url);
  return PLATFORMS.find((entry) => entry.host === host)?.key ?? 'other';
}
